import { getRules } from './Utils';
import getGeometryStyles from './GeometryStyles';
import { processExternalGraphicSymbolizers } from './imageCache';
import { defaultPointStyle } from './styles/static';
import getPointStyle from './styles/pointStyle';
import getLineStyle from './styles/lineStyle';
import getPolygonStyle from './styles/polygonStyle';
import getTextStyle from './styles/textStyle';
import getLinePointStyle from './styles/linePointStyle';
import getPolygonPointStyle from './styles/polygonPointStyle';
import { getScaleForResolution } from '../../utils'

const defaultStyles = [defaultPointStyle];

/**
 * @private
 * Convert symbolizers together with the feature to OL style objects and append them to the styles array.
 * @example appendStyle(styles, point[j], feature, getPointStyle);
 * @param {Array<ol/style>} styles Array of OL styles.
 * @param {object|Array<object>} symbolizers Feature symbolizer object, or array of feature symbolizers.
 * @param {ol/feature} feature OpenLayers feature.
 * @param {Function} styleFunction Function for getting the OL style object. Signature (symbolizer, feature) => OL style.
 * @param {Function} getProperty A property getter: (feature, propertyName) => property value.
 */
function appendStyle(styles, symbolizers, feature, styleFunction, getProperty, isCollection, zIndex) {
    if (Array.isArray(symbolizers)) {
        for (let k = 0; k < symbolizers.length; k += 1) {
            let styleObj = null;

            if (isCollection) {
                styleObj = styleFunction(symbolizers[k], feature, getProperty).clone();
                feature.getGeometry = () => { return feature }
                styleObj.geometryFunction_ = () => { return feature }
            } else {
                styleObj = styleFunction(symbolizers[k], feature, getProperty);
            }

            if (symbolizers[k].zIndex && styleObj.setZIndex) {
                styleObj.setZIndex(symbolizers[k].zIndex);
            }

            if (zIndex) {
                styleObj.setZIndex(zIndex);
            }

            styles.push(styleObj);
        }
    } else {
        let styleObj = null;

        if (isCollection) {
            styleObj = styleFunction(symbolizers, feature, getProperty).clone();
            feature.getGeometry = () => { return feature }
            styleObj.geometryFunction_ = () => { return feature }
        } else {
            styleObj = styleFunction(symbolizers, feature, getProperty);
        }

        if (symbolizers.zIndex && styleObj.setZIndex) {
            styleObj.setZIndex(symbolizers.zIndex);
        }

        if (zIndex) {
            styleObj.setZIndex(zIndex);
        }

        styles.push(styleObj);
    }
}

/**
 * Create openlayers style
 * @example OlStyler(getGeometryStyles(rules), geojson.geometry.type);
 * @param {GeometryStyles} GeometryStyles rulesconverter
 * @param {object|Feature} feature {@link http://geojson.org|geojson}
 *  or {@link https://openlayers.org/en/latest/apidoc/module-ol_Feature-Feature.html|ol/Feature} Changed in 0.0.04 & 0.0.5!
 * @param {Function} getProperty A property getter: (feature, propertyName) => property value.
 * @return ol.style.Style or array of it
 */
export default function OlStyler(GeometryStyles, feature, getProperty) {
    const { polygon, line, point, text } = GeometryStyles;

    if (feature.get('features') != null) {
        feature.set('size', feature.get('features').length);
    }

    const geometry = feature.getGeometry
        ? feature.getGeometry()
        : feature.geometry;
    const geometryType = geometry.getType ? geometry.getType() : geometry.type;

    let styles = [];
    switch (geometryType) {
        case 'Point':
        case 'MultiPoint':
            for (let j = 0; j < point.length; j += 1) {
                appendStyle(styles, point[j], feature, getPointStyle, getProperty);
            }
            for (let j = 0; j < text.length; j += 1) {
                appendStyle(styles, text[j], feature, getTextStyle, getProperty, false, 100);
            }
            break;

        case 'LineString':
        case 'MultiLineString':
            for (let j = 0; j < line.length; j += 1) {
                appendStyle(styles, line[j], feature, getLineStyle, getProperty);
            }
            for (let j = 0; j < point.length; j += 1) {
                appendStyle(styles, point[j], feature, getLinePointStyle, getProperty);
            }
            for (let j = 0; j < text.length; j += 1) {
                appendStyle(styles, text[j], feature, getTextStyle, getProperty, false, 100);
            }
            break;
        case 'Polygon':
        case 'MultiPolygon':
            for (let j = 0; j < polygon.length; j += 1) {
                appendStyle(styles, polygon[j], feature, getPolygonStyle, getProperty);
            }
            for (let j = 0; j < line.length; j += 1) {
                appendStyle(styles, line[j], feature, getLineStyle, getProperty);
            }
            for (let j = 0; j < point.length; j += 1) {
                appendStyle(
                    styles,
                    point[j],
                    feature,
                    getPolygonPointStyle,
                    getProperty
                );
            }
            for (let j = 0; j < text.length; j += 1) {
                appendStyle(styles, text[j], feature, getTextStyle, getProperty, false, 100);
            }
            break;
        case 'Circle':
            for (let j = 0; j < polygon.length; j += 1) {
                appendStyle(styles, polygon[j], feature, getPolygonStyle, getProperty);
            }
            break;
        case 'GeometryCollection':
            let geometries = feature.getGeometry().getGeometries();
            handleGeoemtryCollection(geometries, styles, GeometryStyles, getProperty);
            break;
        default:
            styles = defaultStyles;
    }

    // Set z-index of styles explicitly to fix a bug where GraphicStroke is always rendered above a line symbolizer.
    //styles.forEach((style, index) => style.setZIndex(index));

    return styles;
}

function handleGeoemtryCollection(geometries, styles, GeometryStyles, getProperty) {
    let { polygon, line, point, text } = GeometryStyles;
    geometries.forEach(geometry => {
        let geomType = geometry.getType();
        switch (geomType) {
            case 'Point':
            case 'MultiPoint':
                for (let j = 0; j < point.length; j += 1) {
                    appendStyle(styles, point[j], geometry, getPointStyle, getProperty, true);
                }
                for (let j = 0; j < text.length; j += 1) {
                    appendStyle(styles, text[j], geometry, getTextStyle, getProperty, true, 100);
                }
                break;

            case 'LineString':
            case 'MultiLineString':
                for (let j = 0; j < line.length; j += 1) {
                    appendStyle(styles, line[j], geometry, getLineStyle, getProperty, true);
                }
                for (let j = 0; j < point.length; j += 1) {
                    appendStyle(styles, point[j], geometry, getLinePointStyle, getProperty, true);
                }
                for (let j = 0; j < text.length; j += 1) {
                    appendStyle(styles, text[j], geometry, getTextStyle, getProperty, true, 100);
                }
                break;

            case 'Polygon':
            case 'MultiPolygon':
                for (let j = 0; j < polygon.length; j += 1) {
                    appendStyle(styles, polygon[j], geometry, getPolygonStyle, getProperty, true);
                }
                for (let j = 0; j < line.length; j += 1) {
                    appendStyle(styles, line[j], geometry, getLineStyle, getProperty, true);
                }
                for (let j = 0; j < point.length; j += 1) {
                    appendStyle(
                        styles,
                        point[j],
                        geometry,
                        getPolygonPointStyle,
                        getProperty, true
                    );
                }
                for (let j = 0; j < text.length; j += 1) {
                    appendStyle(styles, text[j], geometry, getTextStyle, getProperty, true, 100);
                }
                break;
        }
    });
}

/**
 * @private
 * Extract feature id from an OpenLayers Feature.
 * @param {Feature} feature {@link https://openlayers.org/en/latest/apidoc/module-ol_Feature-Feature.html|ol/Feature}
 * @returns {string} Feature id.
 */
function getOlFeatureId(feature) {
    return feature.getId();
}

/**
 * @private
 * Extract a property value from an OpenLayers Feature.
 * @param {Feature} feature {@link https://openlayers.org/en/latest/apidoc/module-ol_Feature-Feature.html|ol/Feature}
 * @param {string} propertyName The name of the feature property to read.
 * @returns {object} Property value.
 */
function getOlFeatureProperty(feature, propertyName) {
    return feature.get(propertyName);
}

/**
 * Create an OpenLayers style function from a FeatureTypeStyle object extracted from an SLD document.
 *
 * **Important!** When using externalGraphics for point styling, make sure to call .changed() on the layer
 * inside options.imageLoadedCallback to immediately see the loaded image. If you do not do this, the
 * image icon will only become visible the next time OpenLayers draws the layer (after pan or zoom).
 * @param {FeatureTypeStyle} featureTypeStyle Feature Type Style object.
 * @param {object} options Options
 * @param {function} options.convertResolution An optional function to convert the resolution in map units/pixel to resolution in meters/pixel.
 * When not given, the map resolution is used as-is.
 * @param {function} options.imageLoadedCallback Optional callback that will be called with the url of an externalGraphic when
 * an image has been loaded (successfully or not). Call .changed() inside the callback on the layer to see the loaded image.
 * @param {function} options.getProperty Optional custom property getter: (feature, propertyName) => property value.
 * @returns {Function} A function that can be set as style function on an OpenLayers vector style layer.
 * @example
 * myOlVectorLayer.setStyle(SLDReader.createOlStyleFunction(featureTypeStyle, {
 *   imageLoadedCallback: () => { myOlVectorLayer.changed(); }
 * }));
 */
export function createOlStyleFunction(featureTypeStyle, map, options = {}) {
    const imageLoadedCallback = options.imageLoadedCallback || null;

    // Keep track of whether a callback has been registered per image url.
    const callbackRef = {};

    const currentMap = map;
    return (feature, mapResolution) => {
        // Determine resolution in meters/pixel.
        const currentScale = getScaleForResolution(currentMap.getView().getResolution(), currentMap.getView().getProjection().getCode())

        const getProperty =
            typeof options.getProperty === 'function'
                ? options.getProperty
                : getOlFeatureProperty;

        // Determine applicable style rules for the feature, taking feature properties and current resolution into account.
        const rules = getRules(featureTypeStyle, feature, currentScale, {
            getProperty,
            getFeatureId: getOlFeatureId,
        });

        var defaultImageLoadedCallback = () => { };

        var layerId = feature.layerId != null ? feature.layerId : (feature.get('features') != null && feature.get('features').length > 0) ? feature.get('features')[0].layerId : null;

        if (layerId != null) {
            var layers = currentMap.getLayers().getArray();
            var layer = null;
            for (var i = 0; i < layers.length; i++) {
                if (layers[i].get("uid") == layerId) {
                    layer = layers[i];
                    break;
                }
            }

            if (layer != null) {
                const l = layer;
                defaultImageLoadedCallback = () => {
                    l.getSource().changed();
                }
            }
        }

        var _imageLoadedCallback = imageLoadedCallback != null ? imageLoadedCallback : defaultImageLoadedCallback;

        // Start loading images for external graphic symbolizers and when loaded:
        // * update symbolizers to use the cached image.
        // * call imageLoadedCallback with the image url.
        processExternalGraphicSymbolizers(
            rules,
            featureTypeStyle,
            _imageLoadedCallback,
            callbackRef
        );

        // Convert style rules to style rule lookup categorized by geometry type.
        const geometryStyles = getGeometryStyles(rules);

        // Determine style rule array.
        const olStyles = OlStyler(geometryStyles, feature, getProperty);

        return olStyles;
    };
}
